import _ from "lodash";

import { isRejectedWithValue } from "@reduxjs/toolkit";

import type { Middleware } from "@reduxjs/toolkit";
import { KaboodleToaster } from "components/common";
import { extractErrorMessage } from "utils/error-utils";
import { getSessionId, logToSentry } from "utils/sentry";

const resetErrorDisplayed = _.debounce(() => {
  // only displace single error when backend goes down
  window.hasDisplayedApiError = false;
}, 3000);

declare global {
  interface Window {
    hasDisplayedApiError: boolean;
    navigate404: (path?: string) => void;
    navigateRateLimit: () => void;
    forceLogout: () => void;
    forceReauth: () => void;
    // Provided by useAuthSession once Auth0Provider has mounted. Used by RTK
    // Query's prepareHeaders to fetch a fresh access token at request time
    // (the SDK has its own in-memory cache and refresh logic). Undefined
    // before the Auth0Provider has mounted or after logout.
    getFreshAccessToken: (() => Promise<string>) | undefined;
  }
}

type ErrorPayload = {
  status?: number;
  data?: Record<string, unknown>;
  originalStatus?: string;
};

interface RejectedAction {
  meta?: {
    arg?: {
      endpointName?: string;
    };
  };
  payload: ErrorPayload;
}

/**
 * centralized error handling middleware for RTK Query
 * handles common error statuses and provides toast notifications
 */
export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  // skip error handling for specific endpoints that handle errors themselves
  const skipEndpoints = [
    // intake auth endpoints - 401 means invalid credentials, not expired token
    "loginIntake",
    "registerClientIntake",
    "initiatePasswordReset",
    "confirmPasswordReset",
  ];

  if (isRejectedWithValue(action)) {
    const rejectedAction = action as RejectedAction;
    const endpointName = rejectedAction.meta?.arg?.endpointName;
    const skipErrorHandling = skipEndpoints.some(
      (endpoint) => endpointName && endpointName.includes(endpoint),
    );

    if (!skipErrorHandling) {
      const payload = rejectedAction.payload;
      const httpStatus = payload?.status;

      // handle specific HTTP status codes
      switch (httpStatus) {
        case 404:
          // detailed logging for debugging 404s
          console.error("404 Error Details:", {
            endpoint: endpointName,
            payload,
            action,
            rejectedAction,
            window,
            navigate404: window.navigate404,
          });
          // use global navigate404 function (KaboodleRoot.tsx), navigate to 404 page
          if (window.navigate404 && typeof window.navigate404 === "function") {
            window.navigate404();
          }
          break;

        // rate limiting error
        case 429:
          console.error("Rate Limit Error Details:", {
            endpoint: endpointName,
            payload,
            action,
            rejectedAction,
          });
          // use global navigateRateLimit function to navigate to rate limit page
          if (
            window.navigateRateLimit &&
            typeof window.navigateRateLimit === "function"
          ) {
            window.navigateRateLimit();
          }
          break;

        // auth error (when token expires) - silently re-authenticate or redirect to intake login
        case 401:
          logToSentry("401 received", "info", {
            uiSessionId: getSessionId(),
            endpoint: endpointName,
            path: window.location.pathname,
            isIntakePath: window.location.pathname.startsWith("/intake/"),
          });

          // automatically redirect to intake login if on an intake path,
          // more complex since we need to extract the organization slug from the path
          if (window.location.pathname.startsWith("/intake/")) {
            // extract organization slug from path: /intake/{slug}/...
            const pathParts = window.location.pathname.split("/");
            const organizationSlug = pathParts[2];
            if (organizationSlug) {
              // clear intake token and user from localStorage
              localStorage.removeItem("intakeToken");
              localStorage.removeItem("intakeUser");
              // redirect to intake login
              window.location.href = `/intake/${organizationSlug}/auth/login`;
            }
          } else {
            // main app - try a silent Auth0 re-auth round-trip first; fall back
            // to a hard logout only if forceReauth isn't wired up. The Auth0
            // SSO cookie on the auth domain is first-party for Auth0, so this
            // round-trip is invisible for users whose tenant session is alive
            // even when their refresh token is gone.
            if (
              window.forceReauth &&
              typeof window.forceReauth === "function"
            ) {
              window.forceReauth();
            } else if (
              window.forceLogout &&
              typeof window.forceLogout === "function"
            ) {
              window.forceLogout();
            }
          }
          break;

        // permission error (when user doesn't have permission to perform action)
        case 403:
          if (!window.hasDisplayedApiError) {
            KaboodleToaster.error(
              extractErrorMessage(
                payload,
                "You don't have permission to perform this action.",
              ),
            );
            window.hasDisplayedApiError = true;
            resetErrorDisplayed();
          }
          break;

        case 500:
          if (!window.hasDisplayedApiError) {
            const errorMsg =
              "A server error has occurred. Our development team has been notified. Please try again later.";
            KaboodleToaster.error(errorMsg);
            window.hasDisplayedApiError = true;
            resetErrorDisplayed();
          }
          break;

        case 502:
        case 503:
        case 504:
          if (!window.hasDisplayedApiError) {
            KaboodleToaster.error(
              "The server is currently unavailable. Please try again later.",
            );
            window.hasDisplayedApiError = true;
            resetErrorDisplayed();
          }
          break;

        default:
          // log unexpected errors to console but don't show toast
          console.error("API Error:", action);
      }
    }
  }

  return next(action);
};
